import React, { useContext, useState, useRef } from "react";
import { toast } from "react-toastify";
import { uploadAndGetLocation } from "./utils";
import { UserApiContext } from "../../../../../UserDetailsContext/UserDetailsContext";
import { downloadStandardFormat, readExcelFile, trackUserUploadsMastersFileForBulkUploads } from "../../../Utility/utility";
import Accordion from "./CustomAccordion";

const FM_HEADERS = [
  "S No.", "Origin Name*", "Origin ID", "Origin District", "Origin State*",
  "Destination Name*", "Destination ID", "Destination District", "Destination State*",
  "Truck Type*", "Distance (Km)", "Transit Time (Days)*", "Annual Volume*",
  "Baseline" , "Market Rate", "Site Rate" , "Challenge Price", "Type of Good", "Unit", "Zone",
  "L1 Transporter Freight Rate*", "L1 Transporter Name*",
  "L1 Transporter Share of business (%)*",
  "L2 Transporter Freight Rate", "L2 Transporter Name",
  "L2 Transporter Share of business (%)", "Route Code",
];

const REQUIRED_FIELDS = [
  "Origin Name*", "Origin State*", "Destination Name*", "Destination State*",
  "Truck Type*", "Annual Volume*", "L1 Transporter Freight Rate*", "L1 Transporter Name*"
];

/**
 * Step 1 for file upload process in freight master bulk upload 
 * Component for handling file upload, validation, and downloading standard format.
 */
const UploadFile = ({ setStep = "", setExcel_details = '', companyProfile = {},selectedOrg="company" }) => {
  const [errors, setErrors] = useState([]);
  const { userDetails } = useContext(UserApiContext);
  const fileInputRef = useRef(null);
  const [errorTypes, setErrorTypes] = useState({
    wrongFileType: false,
    headersMismatch: false,
    missingMandatoryFields: false,
    invalidNumericValues: false,
    invalidTransporterShares: false,
    invalidLength: false,
    incompleteTransporterInfo : false
  });

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  /**
   * validates transporter info - if three columns are 
   * present (transporter name, rate and share of business)
   * 
   * @param {Array} headers
   * @param {number} rowIndex
   * @returns {Array} - returns array of errors if present else empty array
   */
  const validateTransporterInfo = (headers, rowIndex) => {
    const errors = [];
    
    const normalizedHeaders = headers.map((header) => header.replace(/\*$/, '').trim());

    const transporterColumns = normalizedHeaders.filter(key => key.startsWith('L') && /\d+/.test(key));
    const transporterNumbers = new Set(transporterColumns.map(key => key.match(/L(\d+)/)[1]));

    transporterNumbers.forEach(num => {
      const prefix = `L${num}`;
      const name = `${prefix} Transporter Name`;
      const rate = `${prefix} Transporter Freight Rate`;
      const share = `${prefix} Transporter Share of business (%)`;

      if (normalizedHeaders.includes(name) || normalizedHeaders.includes(rate) || normalizedHeaders.includes(share)) {
        if (!normalizedHeaders.includes(name) || !normalizedHeaders.includes(rate) || !normalizedHeaders.includes(share)) {
          errors.push(`Row ${rowIndex + 1}: Incomplete information for ${prefix} Transporter. Please provide all three: Name, Freight Rate, and Share of Business.`);
        }
      }
    });

    return errors;
  };

  
  /**
   * Checks if transporter share is 100% or not
   * @param {number} row - row data
   * @param {number} rowIndex - row index
   * @returns {Array} - returns array of errors if present else empty array
   */
  const validateTransporterShares = (row, rowIndex) => {
    const transporterShareColumns = Object.keys(row).filter(key => 
      key.includes("Transporter Share of business") && key.includes("%")
    );

    const sumOfTransporterShares = transporterShareColumns.reduce((sum, key) => {
      const percentage = parseFloat(row[key]);
      return !isNaN(percentage) ? sum + percentage : sum;
    }, 0);

    const EPSILON = 0.00001;
    if (Math.abs(sumOfTransporterShares - 100) > EPSILON) {
      return [`Row ${rowIndex + 1}: Sum of Transporter Share of business should be 100%`];
    }
    return [];
  };


  /**
   * Validations for uploaded excel file
   * @param {Array} data - excel data
   * @returns {Array} - returns true if errors are present else false
   */
  const validateData = (data) => {
    let allErrors = [];
    let newErrorTypes = {};

    if (data.length === 0) {
      toast.info("Please Upload a valid File.");
      return false;
    }

    const headers = Object.keys(data[0]);
    
    const missingHeaders = REQUIRED_FIELDS.filter(
      (header) => !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
      newErrorTypes.headersMismatch = true;
    }

    data.forEach((row, index) => {
      REQUIRED_FIELDS.forEach((field) => {
        if (!row[field]) {
          allErrors.push(`Row ${index + 1}: ${field} cannot be empty`);
          newErrorTypes.missingMandatoryFields = true;  
        }
      });

      // Check length of origin, destination, and route code
      [ "Origin ID", "Destination ID", "Route Code"].forEach((col) => {
        if (row[col] && row[col].toString().trim().length < 3) {
          allErrors.push(
            `Row ${index + 1}: ${col} should be at least 3 characters long`
          );
          newErrorTypes.invalidLength = true;
        }
      });

      [ "Baseline", "Annual Volume*", "Transit Time (Days)*", "Distance (Km)","Market Rate", "Site Rate" , "Challenge Price"].forEach((col) => {
        if (row[col] && row[col] !== "" && isNaN(parseFloat(row[col]))) {
          allErrors.push(`Row ${index + 1}: ${col} must be a number`);
          newErrorTypes.invalidNumericValues = true;
        }
      });

      const transporterErrors = validateTransporterInfo(Object.keys(row), index);
      allErrors = [...allErrors, ...transporterErrors];
      if (transporterErrors.length > 0) {
        newErrorTypes.incompleteTransporterInfo = true;
      }

      // Validate transporter shares
      const shareErrors = validateTransporterShares(row, index);
      allErrors = [...allErrors, ...shareErrors];
      if (shareErrors.length > 0) {
        newErrorTypes.invalidTransporterShares = true;
      }
    });

    setErrors(allErrors);
    setErrorTypes(newErrorTypes);
    return allErrors.length === 0;
  };

  /**
   * handles file upload and security checks 
   * @param {any} e  -event for file upload
   * @returns {void}
   */
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      resetFileAndErrors()

      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorTypes((prev) => ({ ...prev, wrongFileType: true }));
        setErrors(["The file must be in .xlsx or .xls format."]);
        return;
      }

      const { data, error } = await readExcelFile(selectedFile);

      if (error) {
        toast.error(error);
        return;
      }

      if (validateData(data)) {
        setStep(1);
        setExcel_details(data);
        await uploadAndGetLocation(selectedFile, userDetails, companyProfile, setStep, setExcel_details);
      } else {
        setIsAccordionOpen(false)
      }

      trackUserUploadsMastersFileForBulkUploads(
        "Freight",
        userDetails,
        companyProfile.domain_data.company_id,
        selectedFile.name
      );
    }
  };

  /**
   * Reset file input, errors and error types 
   * @returns {void}
   */
  const resetFileAndErrors = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setErrors([]);
    setErrorTypes({
      wrongFileType: false,
      headersMismatch: false,
      missingMandatoryFields: false,
      invalidNumericValues: false,
      invalidTransporterShares: false,
      invalidLength: false,
      incompleteTransporterInfo : false
    });
  };

  return (
    <div className="p-0 px-3 card-body" style={{ height: "460px", overflow: "scroll" }}>
      <div className="py-4 p-2 text-center d-flex justify-content-around align-items-center sticky-top" style={{backgroundColor: "white"}}>
        <button
          className="btn btn-primary"
          onClick={() => downloadStandardFormat(FM_HEADERS, "FreightMaster")}
        >
          Download Standard Format
        </button>

        <div className="text-center">
          <label className="btn btn-success upload-file-button-enquiry-my-account">
            <input
              type="file"
              id="upload_file_button"
              onChange={handleFileUpload}
              className="d-none"
              accept=".xlsx,.xls"
              ref={fileInputRef}
            />
            <i className="fas fa-upload"></i> Upload File
          </label>
        </div>
      </div>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        <h5 className="alert-heading under">
          <u>File Requirements</u>:
        </h5>
        <ul>
          <li
            style={{
              color: errorTypes.wrongFileType ? "red" : "inherit",
              fontWeight: errorTypes.wrongFileType ? "bold" : "normal",
            }}
          >
            The file must be in .xlsx or .xls format.
            {errorTypes.wrongFileType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.headersMismatch ? "red" : "inherit",
              fontWeight: errorTypes.headersMismatch ? "bold" : "normal",
            }}
          >
            Ensure that headers match the standard format.
            {errorTypes.headersMismatch && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.missingMandatoryFields ? "red" : "inherit",
              fontWeight: errorTypes.missingMandatoryFields ? "bold" : "normal",
            }}
          >
            All fields marked with * are mandatory.
            {errorTypes.missingMandatoryFields && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
            <ul>
            <li>
                {FM_HEADERS
                  .filter(header => header.endsWith("*") && !header.includes("Transporter"))
                  .map((header, index, array) => (
                    <React.Fragment key={index}>
                      <span>{header}</span>
                      {index < array.length - 1 && ", "}
                    </React.Fragment>
                ))}
            </li>
            </ul>
          </li>
          <li
            style={{
              color: errorTypes.invalidTransporterShares ? "red" : "inherit",
              fontWeight: errorTypes.invalidTransporterShares
                ? "bold"
                : "normal",
            }}
          >
            Sum of Transporter Share of business should be 100%.
            {errorTypes.invalidTransporterShares && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidLength ? "red" : "inherit",
              fontWeight: errorTypes.invalidLength ? "bold" : "normal",
            }}
          >
            Origin, Destination, and Route Code, <u>if provided</u>, should be at least 3 characters
            long.
            {errorTypes.invalidLength && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidNumericValues ? "red" : "inherit",
              fontWeight: errorTypes.invalidNumericValues ? "bold" : "normal",
            }}
          >
            Baseline, Volume, Transit Time, and Distance must be numbers.
            {errorTypes.invalidNumericValues && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li style={{
            color: errorTypes.incompleteTransporterInfo ? "red" : "inherit",
            fontWeight: errorTypes.incompleteTransporterInfo ? "bold" : "normal",
          }}>
            For each L(n) Transporter, all three columns (Name, Freight Rate, Share of Business) must be present.
            {errorTypes.incompleteTransporterInfo && (
              <i className="fa fa-exclamation-circle" style={{ color: "red" }} aria-hidden="true"></i>
            )}
          </li>
        </ul>
      </div>
      {/* <div className="mt-3 p-3 bg-light border rounded">
        <p className="mb-0">
          <strong>Tip for Multiple Transporters:</strong>
        </p>
        <p className="mb-0">
          While the standard format shows fields for 2 transporters (L1 and L2), you can add more! 
          Simply continue the pattern with L3, L4, etc. For each new transporter, include:
        </p>
        <ul className="mb-0">
          <li>Transporter Name</li>
          <li>Freight Rate</li>
          <li>Share of Business (%)</li>
        </ul>
        <p className="mb-0 mt-2">
          <em>Example: L3 Transporter Name, L3 Transporter Freight Rate, L3 Transporter Share of business (%)</em>
        </p>
      </div> */}

      <div className="pt-3">
        <Accordion  on title="Tip for Multiple Transporters" isOpen = {isAccordionOpen} setIsOpen={setIsAccordionOpen}>
          <div className="">
            <p className="mb-0">
              While the standard format shows fields for 2 transporters (L1 and L2), you can add more!
              Simply continue the pattern with L3, L4, etc. For each new transporter, include:
            </p>
            <ul className="mb-0">
              <li>Transporter Name</li>
              <li>Freight Rate</li>
              <li>Share of Business (%)</li>
            </ul>
            <p className="mb-0 mt-2">
              <em>Example: L3 Transporter Name, L3 Transporter Freight Rate, L3 Transporter Share of business (%)</em>
            </p>
          </div>
        </Accordion>
      </div>

      {errors.length > 0 && (
        <div>
          <h6 className="text-danger">Errors:</h6>
          <ul className="list-group">
            {errors.map((error, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-danger"
              >
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadFile;